import { Fragment, useState } from "react";
import { Anchor, Badge, Group, Paper, Stack, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { AudienceTargets, HeadsetRelease } from "types/headsetReleaseTypes";
import { AudienceChips } from "./AudienceChips";
import { ExpandToggle } from "./ExpandToggle";
import { HardwareChips } from "./HardwareChips";
import { LatestBadge } from "./LatestBadge";
import { releasePagePath } from "./releaseRoutes";
import { ReleaseHeadsets } from "./ReleaseHeadsets";
import { UpdateProgress } from "./UpdateProgress";

interface ReleasesTableProps {
  releases: HeadsetRelease[];
  targets?: AudienceTargets;
}

export function ReleasesTable({ releases, targets }: ReleasesTableProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string>();

  return (
    <Paper withBorder radius="md">
      <Table.ScrollContainer minWidth={980}>
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={150}>{t("headsetReleases.column.version")}</Table.Th>
              <Table.Th w={200}>{t("headsetReleases.column.hardware")}</Table.Th>
              <Table.Th>{t("headsetReleases.column.assignedTo")}</Table.Th>
              <Table.Th w={220}>{t("headsetReleases.column.headsetsUpdated")}</Table.Th>
              <Table.Th w={70} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {releases.map((release) => {
              const isOpen = expanded === release.version;
              const started = release.status === "started";
              return (
                <Fragment key={release.version}>
                  <Table.Tr>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <ExpandToggle
                          open={isOpen}
                          name={release.version}
                          onToggle={() => setExpanded(isOpen ? undefined : release.version)}
                        />
                        <Stack gap={2}>
                          <Text fw={700}>{release.version}</Text>
                          <Group gap={4}>
                            {release.isLatest && <LatestBadge />}
                            {!started && (
                              <Badge size="xs" variant="outline" color="gray">
                                {t(`headsetReleases.releaseStatus.${release.status}`)}
                              </Badge>
                            )}
                          </Group>
                        </Stack>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <HardwareChips release={release} />
                    </Table.Td>
                    <Table.Td>
                      <AudienceChips
                        audience={release.audience}
                        targets={targets}
                        isLatest={release.isLatest}
                      />
                    </Table.Td>
                    <Table.Td>
                      {release.progress && (
                        <UpdateProgress progress={release.progress.headsets} started={started} />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={releasePagePath(release.version)} fw={600}>
                        {t("headsetReleases.open")}
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                  {isOpen && (
                    <Table.Tr>
                      <Table.Td colSpan={5} pl={48}>
                        <ReleaseHeadsets version={release.version} started={started} />
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {!releases.length && (
        <Text c="dimmed" p="md">
          {t("headsetReleases.noReleases")}
        </Text>
      )}
    </Paper>
  );
}
